import axios, {
  type AxiosRequestConfig,
  AxiosError,
  type AxiosResponse,
} from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export const TestType = {
  QUIZ: "QUIZ",
  ASSESSMENT: "ASSESSMENT",
} as const;
export type TestType = (typeof TestType)[keyof typeof TestType];

export const TestDifficulty = {
  BEGINNER: "BEGINNER",
  INTERMEDIATE: "INTERMEDIATE",
  ADVANCED: "ADVANCED",
  EXPERT: "EXPERT",
} as const;
export type TestDifficulty =
  (typeof TestDifficulty)[keyof typeof TestDifficulty];

export const TestQuestionType = {
  TRUE_FALSE: "TRUE_FALSE",
  SINGLE_SELECT: "SINGLE_SELECT",
  MULTIPLE_SELECT: "MULTIPLE_SELECT",
  FILL_BLANK: "FILL_BLANK",
  SHORT_ANSWER: "SHORT_ANSWER",
  ESSAY: "ESSAY",
} as const;
export type TestQuestionType =
  (typeof TestQuestionType)[keyof typeof TestQuestionType];

export const TestAttemptStatus = {
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  ABANDONED: "ABANDONED",
  EXPIRED: "EXPIRED",
  NOT_STARTED: "NOT_STARTED",
  TIMED_OUT: "TIMED_OUT",
  UNDER_REVIEW: "UNDER_REVIEW",
} as const;
export type TestAttemptStatus =
  (typeof TestAttemptStatus)[keyof typeof TestAttemptStatus];

export const GradingType = {
  AUTOMATIC: "AUTOMATIC",
  MANUAL: "MANUAL",
  MIXED: "MIXED",
} as const;
export type GradingType = (typeof GradingType)[keyof typeof GradingType];

// ============================================================================
// DTOs & REQUEST INTERFACES
// ============================================================================

export interface CreateTestDto {
  title: string;
  description?: string;
  type: TestType;
  difficulty: TestDifficulty;
  timeLimit?: number;
  maxAttempts?: number;
  passingScore: number;
  showCorrectAnswers: boolean;
  allowRetakes: boolean;
  enableRandomization: boolean;
  questionsPerTest: number;
  useQuestionSetId?: string;
  gradingType: GradingType;
  learningMaterialId?: string;
  courseId?: string;
}

export interface UpdateTestDto extends Partial<CreateTestDto> {
  id: string;
}

export interface TestSearchParams {
  courseId?: string;
  materialId?: string;
  type?: TestType;
  difficulty?: TestDifficulty;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface CreateQuestionSetDto {
  name: string;
  description?: string;
  testId: string;
  weight: number;
  tags?: string[];
}

export interface UpdateQuestionSetDto extends Partial<CreateQuestionSetDto> {
  id: string;
}

export interface QuestionOption {
  id: string;
  text: string;
  isCorrect?: boolean;
}

export interface QuestionBody {
  question: string;
  type: TestQuestionType;
  difficulty: TestDifficulty;
  options?: QuestionOption[];
  correctAnswer: { id: string; text: string };
  explanation?: string;
  hints?: string[];
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  points: number;
  negativeMarks?: number;
  tags?: string[];
}

export interface CreateQuestionDto {
  body: QuestionBody[];
  testId?: string;
  questionSetId?: string;
}

export interface UpdateQuestionDto extends Partial<CreateQuestionDto> {
  id: string;
}

export interface StartTestRequest {
  testId: string;
}

export interface SubmitAnswerRequest {
  questionId: string;
  userAnswer: string | string[] | boolean | number;
  timeSpent: number;
}

export interface MyAttemptsParams {
  testId?: string;
  status?: string;
  page?: number;
  limit?: number;
}

// ============================================================================
// RESPONSE INTERFACES
// ============================================================================

export interface ApiResponse<T = Record<string, unknown>> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface TestAttemptResponse {
  attemptId: string;
  testId: string;
  attemptNumber: number;
  status: TestAttemptStatus;
  questions: TestQuestionResponse[];
  timeRemaining?: number;
  totalQuestions: number;
}

export interface TestQuestionResponse {
  id: string;
  question: string;
  type: TestQuestionType;
  options?: QuestionOption[];
  points: number;
  timeLimit?: number;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
}

export interface TestResultResponse {
  attemptId: string;
  status: TestAttemptStatus;
  totalQuestions: number;
  answeredQuestions: number;
  correctAnswers: number;
  rawScore: number;
  percentage: number;
  passed: boolean;
  timeSpent: number;
  feedback: string;
  canRetake: boolean;
  nextAttemptAllowed: boolean;
}

export interface SubmitAnswerResponse {
  success: boolean;
  isCorrect: boolean | null;
  pointsEarned: number;
  maxPoints: number;
  needsManualGrade: boolean;
}

export interface TestPreviewData {
  id: string;
  title: string;
  description?: string;
  type: TestType;
  difficulty: TestDifficulty;
  timeLimit?: number;
  maxAttempts?: number;
  passingScore: number;
  totalQuestions: number;
  allowRetakes: boolean;
}

// ============================================================================
// API CLIENT CLASS
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const testApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/testing`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(testApiClient);

testApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const errorData = error.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const rawMessage = errorData?.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(" ")
      : rawMessage || error.message || "An error occurred";
    const apiError = {
      message,
      statusCode: error.response?.status || 0,
      error: errorData?.error,
    };
    return Promise.reject(apiError);
  },
);

class TestApiClient {
  setToken(token: string): void {
    if (token) {
      testApiClient.defaults.headers["Authorization"] = `Bearer ${token}`;
    } else {
      delete testApiClient.defaults.headers["Authorization"];
    }
  }

  // Generic API request method
  private async request<T>(
    endpoint: string,
    options: Partial<AxiosRequestConfig> = {}
  ): Promise<ApiResponse<T>> {
    const response = await testApiClient({
      url: endpoint,
      method: options.method || "GET",
      data: options.data,
      params: options.params,
      headers: options.headers,
      ...options,
    });
    return response.data as ApiResponse<T>;
  }

  // ============================================================================
  // TEST MANAGEMENT METHODS (ADMIN ONLY)
  // ============================================================================

  async createTest(data: CreateTestDto): Promise<ApiResponse> {
    return this.request("/tests", {
      method: "POST",
      data: data,
    });
  }

  async searchTests(params: TestSearchParams = {}): Promise<ApiResponse> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/tests?${queryString}` : "/tests";

    return this.request(endpoint);
  }

  async getTest(testId: string): Promise<ApiResponse> {
    return this.request(`/tests/${testId}`);
  }

  async updateTest(
    testId: string,
    data: Partial<CreateTestDto>
  ): Promise<ApiResponse> {
    return this.request(`/tests/${testId}`, {
      method: "PUT",
      data,
    });
  }

  async deleteTest(testId: string): Promise<ApiResponse> {
    return this.request(`/tests/${testId}`, {
      method: "DELETE",
    });
  }

  // ============================================================================
  // QUESTION SET MANAGEMENT METHODS (ADMIN ONLY)
  // ============================================================================

  async createQuestionSet(data: CreateQuestionSetDto): Promise<ApiResponse> {
    return this.request("/question-sets", {
      method: "POST",
      data,
    });
  }

  async getQuestionSets(testId: string): Promise<ApiResponse> {
    return this.request(`/tests/${testId}/question-sets`);
  }

  async updateQuestionSet(
    setId: string,
    data: Partial<CreateQuestionSetDto>
  ): Promise<ApiResponse> {
    return this.request(`/question-sets/${setId}`, {
      method: "PUT",
      data,
    });
  }

  // ============================================================================
  // QUESTION MANAGEMENT METHODS (ADMIN ONLY)
  // ============================================================================

  async createQuestion(data: CreateQuestionDto): Promise<ApiResponse> {
    return this.request("/questions", {
      method: "POST",
      data,
    });
  }

  async getQuestionsForTest(testId: string): Promise<ApiResponse> {
    return this.request(`/tests/${testId}/questions`);
  }

  async updateQuestion(
    questionId: string,
    data: Partial<CreateQuestionDto>
  ): Promise<ApiResponse> {
    return this.request(`/questions/${questionId}`, {
      method: "PUT",
      data,
    });
  }

  async deleteQuestion(questionId: string): Promise<ApiResponse> {
    return this.request(`/questions/${questionId}`, {
      method: "DELETE",
    });
  }

  // ============================================================================
  // TEST TAKING WORKFLOW METHODS
  // ============================================================================

  async startTest(
    data: StartTestRequest
  ): Promise<ApiResponse<TestAttemptResponse>> {
    return this.request("/attempts/start", {
      method: "POST",
      data,
    });
  }

  async submitAnswer(
    attemptId: string,
    data: SubmitAnswerRequest
  ): Promise<ApiResponse<SubmitAnswerResponse>> {
    return this.request(`/attempts/${attemptId}/answers`, {
      method: "POST",
      data,
    });
  }

  async completeTest(
    attemptId: string
  ): Promise<ApiResponse<TestResultResponse>> {
    return this.request(`/attempts/${attemptId}/complete`, {
      method: "POST",
    });
  }

  async certification(courseId: string): Promise<ApiResponse> {
    return this.request(`/certification?courseId=${courseId}`, {
      method: "GET",
    });
  }

  /**
   * Absolute URL of the server-rendered certificate PDF, carrying a short-lived
   * signed token instead of the Authorization header so it also works from
   * in-app browsers and download managers.
   */
  async certificationPdfUrl(
    courseId: string,
    disposition: "attachment" | "inline" = "attachment",
  ): Promise<string> {
    const response = await this.request<{ token: string }>(
      "/certification/pdf-link",
      { method: "POST", params: { courseId } },
    );
    const token = response.data?.token;
    if (!token) throw new Error("No certificate download link returned");
    const query = disposition === "inline" ? "?disposition=inline" : "";
    return `${API_BASE_URL}/api/testing/certification/file/${encodeURIComponent(token)}${query}`;
  }

  // ============================================================================
  // STUDENT TEST HISTORY & RESULTS METHODS
  // ============================================================================

  async getMyTestAttempts(params: MyAttemptsParams = {}): Promise<ApiResponse> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/my-attempts?${queryString}`
      : "/my-attempts";

    return this.request(endpoint);
  }

  async getAttemptResults(attemptId: string): Promise<ApiResponse> {
    return this.request(`/attempts/${attemptId}/results`);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  async previewTest(testId: string): Promise<ApiResponse<TestPreviewData>> {
    return this.request(`/tests/${testId}/preview`);
  }

  async healthCheck(): Promise<ApiResponse> {
    return this.request("/health");
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  // Helper method to handle test workflow
  async conductTestSession(testId: string): Promise<{
    attempt: TestAttemptResponse;
    submitAnswer: (
      questionId: string,
      userAnswer: string | string[] | boolean | number,
      timeSpent: number
    ) => Promise<ApiResponse<SubmitAnswerResponse>>;
    completeTest: () => Promise<ApiResponse<TestResultResponse>>;
  }> {
    try {
      // Start the test
      const startResponse = await this.startTest({ testId });
      const attempt = startResponse.data;

      return {
        attempt,
        submitAnswer: (
          questionId: string,
          userAnswer: string | string[] | boolean | number,
          timeSpent: number
        ) =>
          this.submitAnswer(attempt.attemptId, {
            questionId,
            userAnswer,
            timeSpent,
          }),
        completeTest: () => this.completeTest(attempt.attemptId),
      };
    } catch (error) {
      console.error("Failed to start test session:", error);
      throw error;
    }
  }

  // Helper method to get full test details with questions
  async getFullTestDetails(testId: string): Promise<{
    test: Record<string, unknown>;
    questions: Record<string, unknown>;
  }> {
    try {
      const [testResponse, questionsResponse] = await Promise.all([
        this.getTest(testId),
        this.getQuestionsForTest(testId),
      ]);

      return {
        test: testResponse.data,
        questions: questionsResponse.data,
      };
    } catch (error) {
      console.error("Failed to get full test details:", error);
      throw error;
    }
  }

  // Helper method for paginated search with loading state
  async searchTestsPaginated(
    params: TestSearchParams = {},
    signal?: AbortSignal
  ): Promise<ApiResponse | null> {
    try {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });

      const response = await fetch(`/api/testing/tests?${searchParams}`, {
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result as ApiResponse;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return null;
      }
      throw error;
    }
  }
}

// ============================================================================
// EXPORT API INSTANCE
// ============================================================================

export const testApi = new TestApiClient();
export const TestApiClientClass = TestApiClient;

// ============================================================================
// ERROR CLASSES
// ============================================================================

export class TestApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TestApiError";
  }
}

export class TestValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TestValidationError";
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const testUtils = {
  // Format time remaining for display
  formatTimeRemaining: (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  },

  // Calculate percentage score
  calculatePercentage: (earned: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((earned / total) * 100 * 100) / 100; // Round to 2 decimal places
  },

  // Get difficulty color for UI
  getDifficultyColor: (difficulty: TestDifficulty): string => {
    switch (difficulty) {
      case TestDifficulty.BEGINNER:
        return "green";
      case TestDifficulty.INTERMEDIATE:
        return "yellow";
      case TestDifficulty.ADVANCED:
        return "red";
      default:
        return "gray";
    }
  },

  // Get status color for UI
  getStatusColor: (status: TestAttemptStatus): string => {
    switch (status) {
      case TestAttemptStatus.IN_PROGRESS:
        return "blue";
      case TestAttemptStatus.COMPLETED:
        return "green";
      case TestAttemptStatus.ABANDONED:
        return "orange";
      case TestAttemptStatus.EXPIRED:
        return "red";
      default:
        return "gray";
    }
  },

  // Validate question body
  validateQuestionBody: (body: QuestionBody): string[] => {
    const errors: string[] = [];

    if (!body.question?.trim()) {
      errors.push("Question text is required");
    }

    if (!body.type) {
      errors.push("Question type is required");
    }

    if (!body.points || body.points < 0) {
      errors.push("Points must be a positive number");
    }

    if (
      body.type === TestQuestionType.SINGLE_SELECT ||
      body.type === TestQuestionType.MULTIPLE_SELECT
    ) {
      if (
        !body.options ||
        !Array.isArray(body.options) ||
        body.options.length < 2
      ) {
        errors.push("Multiple choice questions must have at least 2 options");
      }
    }

    return errors;
  },
};
